import { StyledAddVerifierButton } from 'components/v2/buttons';
import { COLLABORATION_REQUEST_SENT_TEXT } from 'components/v2/compiler/constants';
import { Panel } from 'primereact/panel';
import React from 'react';
import styled from 'styled-components';
import { defaultThemeColors } from 'styles/theme';
import { isWithin24Hours } from 'util/utils';

interface VerifierCompanyCardProps {
  data: any;
  onSelectVerifier: any;
  onAddVerifier: any;
  expanded: boolean;
  buttonsAreDisabled: boolean | undefined;
}

const VerifierCompanyCard: React.FunctionComponent<VerifierCompanyCardProps> = ({
  data,
  onSelectVerifier,
  onAddVerifier,
  expanded,
  buttonsAreDisabled,
}) => {
  const { id, name, email, information, verifierType, requestSentAt, successOperationText, errorText } = data;

  const collaborationRequestDate = !!requestSentAt ? new Date(requestSentAt) : undefined;

  const requestCollaborationButtonDisabledText =
    successOperationText ?? (isWithin24Hours(collaborationRequestDate) ? COLLABORATION_REQUEST_SENT_TEXT : null);

  const headerTemplate = (options: any) => {
    const className = `${options.className} justify-content-space-between w-full`;
    return (
      <div className={className} onClick={options.onTogglerClick}>
        <div>
          <strong>{name}</strong>
          <StyledCompanyInfo style={{ marginBottom: 0 }}>{verifierType}</StyledCompanyInfo>
        </div>
      </div>
    );
  };

  const renderContactInformation = () => {
    if (!!information && !!email) {
      return (
        <StyledCompanyInfo className="p-component">
          {information}
          <br />
          {email}
        </StyledCompanyInfo>
      );
    }
    if (!!information && !email) {
      return <StyledCompanyInfo className="p-component">{information}</StyledCompanyInfo>;
    }
    if (!information && !!email) {
      return <StyledCompanyInfo className="p-component">{email}</StyledCompanyInfo>;
    }
    return null;
  };

  return (
    <StyledVerifierCompanyCardPanel
      headerTemplate={(options) => headerTemplate(options)}
      toggleable
      collapsed={!expanded}
      onToggle={onSelectVerifier}
    >
      <div onClick={onSelectVerifier}>
        {renderContactInformation()}
        <StyledAddVerifierButton
          label="Add verifier"
          onClick={(e) => {
            e.stopPropagation();
            onAddVerifier(id);
          }}
          disabled={buttonsAreDisabled}
        />
        {requestCollaborationButtonDisabledText && (
          <StyledSuccessTextContainer>{requestCollaborationButtonDisabledText}</StyledSuccessTextContainer>
        )}
        {errorText && <StyledErrorTextContainer>{errorText}</StyledErrorTextContainer>}
      </div>
    </StyledVerifierCompanyCardPanel>
  );
};

const StyledVerifierCompanyCardPanel = styled(Panel)`
  border: 1px solid;
  border-color: ${defaultThemeColors.elementsInputBorder};
  border-radius: 2px;

  .p-panel-header {
    padding-left: 1rem;
    padding-right: 1rem;
    border: 0;
    background-color: ${defaultThemeColors.primaryWhite};
    ${(props) => props.theme.typography.bodyInput};
    color: ${(props) => props.theme.colors.primaryBlack};
  }

  &.p-panel.p-panel-toggleable .p-panel-header {
    padding: 1rem;
  }

  .p-panel-content {
    padding: 1rem;
    border: 0;
    background-color: ${defaultThemeColors.primaryWhite};
    ${(props) => props.theme.typography.bodyInput};
    padding-top: 0px;
  }
`;

const StyledSuccessTextContainer = styled.div`
  margin-top: 15px;
  font-size: 10px;
  font-style: italic;
  color: ${defaultThemeColors.primaryGreen};
`;

const StyledErrorTextContainer = styled.div`
  margin-top: 15px;
  color: red;
`;

const StyledCompanyInfo = styled.p`
  ${(props) => props.theme.typography.bodyInput};
  margin-top: 0.5rem;
`;

export default VerifierCompanyCard;
